import axios from 'axios'
import { env } from '../configs/Config'

let isRefreshing = false
let requestQueue = []

function clearSessionAndRedirect() {
  localStorage.clear()
  window.location.href = '/'
}

function flushQueue(error, token) {
  requestQueue.forEach((item) => {
    if (error) {
      item.reject(error)
      return
    }
    item.resolve(token)
  })
  requestQueue = []
}

async function callRefreshToken() {
  const refreshToken = localStorage.getItem('refresh_token')
  const accessToken = localStorage.getItem('access_token')
  if (!refreshToken) {
    throw new Error('Missing refresh token')
  }

  const response = await axios.post(
    `${env.api_url}/auth/refresh-token`,
    { refreshToken },
    {
      headers: {
        Authorization: `Bearer ${accessToken || refreshToken || ''}`,
        'content-type': 'application/json; charset=utf-8'
      }
    }
  )

  const data = response?.data || {}
  const nextAccessToken = data?.token || data?.access_token
  const nextWebToken = data?.tokenWeb || data?.token_web
  const nextRefreshToken = data?.refreshToken || data?.refresh_token

  if (!nextAccessToken && !nextWebToken) {
    throw new Error('Refresh token response is invalid')
  }

  if (nextAccessToken) {
    localStorage.setItem('access_token', nextAccessToken)
  }
  if (nextWebToken) {
    localStorage.setItem('tokenWeb', nextWebToken)
  }
  if (nextRefreshToken) {
    localStorage.setItem('refresh_token', nextRefreshToken)
  }

  return {
    accessToken: nextAccessToken || localStorage.getItem('access_token'),
    webToken: nextWebToken || localStorage.getItem('tokenWeb')
  }
}

function canAttemptRefresh(error, originalRequest) {
  if (error?.response?.status !== 401) {
    return false
  }
  if (!originalRequest || originalRequest._retry) {
    return false
  }
  const url = `${originalRequest.url || ''}`.toLowerCase()
  if (url.includes('/auth/login') || url.includes('/auth/refresh-token')) {
    return false
  }
  return true
}

export function attachAuthInterceptors(instance, options = {}) {
  const { defaultTokenKey = 'access_token' } = options

  instance.interceptors.request.use(
    (config) => {
      const tokenKey = config.tokenKey || defaultTokenKey
      const token = localStorage.getItem(tokenKey)

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }

      config._tokenKey = tokenKey
      delete config.tokenKey
      return config
    },
    (error) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    (response) => {
      if (response && response.data) {
        return response.data
      }
      return response
    },
    async (error) => {
      const originalRequest = error?.config

      if (!canAttemptRefresh(error, originalRequest)) {
        if (error?.response?.status === 401) {
          clearSessionAndRedirect()
        }
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          requestQueue.push({
            resolve: (token) => {
              if (originalRequest?.headers && token) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(instance(originalRequest))
            },
            reject
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const tokenSet = await callRefreshToken()
        const requestTokenKey =
          originalRequest?._tokenKey || defaultTokenKey || 'access_token'
        const retryToken =
          requestTokenKey === 'tokenWeb'
            ? tokenSet.webToken
            : tokenSet.accessToken

        flushQueue(null, retryToken)

        if (originalRequest?.headers && retryToken) {
          originalRequest.headers.Authorization = `Bearer ${retryToken}`
        }

        return instance(originalRequest)
      } catch (refreshError) {
        flushQueue(refreshError, null)
        clearSessionAndRedirect()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
  )
}
